import { useCallback, useEffect, useState } from "react";

/**
 * Detects that a newer build has been deployed while the app was open.
 *
 * Installed to a phone's home screen the app runs in standalone display mode:
 * no address bar, no reload button, and no pull-to-refresh. iOS suspends and
 * resumes the same document rather than reloading it, so a deploy can go out
 * and the phone will keep running the old bundle until the app is force-closed.
 * That is the whole problem this solves.
 *
 * How it knows: CRA writes build/asset-manifest.json listing the hashed bundle
 * filenames. The hash changes on every build whose output changed, so comparing
 * the manifest's main.js against the <script> the page actually loaded is an
 * exact "am I running the current build?" test, with no version file to remember to
 * bump, and no false positive from a redeploy that produced identical output.
 *
 * index.html is served with `max-age=0, must-revalidate` and there is no service
 * worker, so a plain reload really does pick the new build up.
 */

/** The main bundle this page was actually loaded with, from its script tag. */
function loadedMainBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[src*="/static/js/main."]');
  if (!el) return null;
  try {
    return new URL(el.src, window.location.origin).pathname;
  } catch {
    return null;
  }
}

async function deployedMainBundle(): Promise<string | null> {
  // no-store, or the check itself would be answered from cache and never notice
  // anything. Same-origin, so the app's connect-src 'self' CSP allows it.
  const res = await fetch("/asset-manifest.json", { cache: "no-store" });
  if (!res.ok) return null;
  const manifest = await res.json();
  return manifest?.files?.["main.js"] ?? null;
}

/** True when running as an installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari predates the media query and still uses this.
    (window.navigator as any).standalone === true
  );
}

const POLL_MS = 5 * 60 * 1000;

export function useAppUpdate() {
  const [updateReady, setUpdateReady] = useState(false);

  const check = useCallback(async () => {
    const loaded = loadedMainBundle();
    // In development there is no manifest and no hashed bundle; nothing to do.
    if (!loaded) return;
    try {
      const deployed = await deployedMainBundle();
      if (deployed && deployed !== loaded) setUpdateReady(true);
    } catch {
      // Offline or a blip. Silence is right here, since this is a background check,
      // and an error banner about a failed update check helps nobody.
    }
  }, []);

  useEffect(() => {
    check();

    // Coming back to the app is the moment a deploy is most likely to have
    // happened while it sat suspended, so it matters more than the timer.
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = window.setInterval(check, POLL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(timer);
    };
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateReady, reload, check };
}
